/** Inline stroke icons (Lucide-style), matching the prototype set. */
import type { ReactElement, SVGProps } from "react";

// An <svg> carrying a viewBox but no width/height has no intrinsic size, so it expands to
// fill its container. Most call sites are saved by a container-specific CSS rule (.btn svg,
// .nav a svg, .banner svg …); anywhere without one the icon rendered enormous. This default
// sits before {...props}, so a caller can still override width/height or pass a className.
const s = (path: ReactElement) =>
  function Icon(props: SVGProps<SVGSVGElement>) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={18}
        height={18}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        {...props}
      >
        {path}
      </svg>
    );
  };

export const ShieldCheck = s(
  <>
    <path d="M12 3l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </>,
);
export const Grid = s(
  <>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </>,
);
export const Plus = s(<path d="M12 5v14M5 12h14" />);
export const Menu = s(<path d="M4 6h16M4 12h16M4 18h16" />);
export const Rows = s(<path d="M4 6h16M7 12h10M10 18h4" />);
// Rows (three shrinking bars) reads as "filter", so file surfaces get real glyphs:
// FileText for a document row, UploadCloud for a dropzone.
export const FileText = s(
  <>
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5z" />
    <path d="M14 3v5h5M9 13h6M9 17h6" />
  </>,
);
export const UploadCloud = s(
  <>
    <path d="M4 14.9A7 7 0 1115.7 8h1.8a4.5 4.5 0 012.5 8.2" />
    <path d="M12 12v9M8 16l4-4 4 4" />
  </>,
);
export const Users = s(
  <>
    <circle cx="12" cy="8" r="3.2" />
    <path d="M5 20c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5" />
  </>,
);
/** Single person — Profile nav item (Users above is the plural/team icon). */
export const User = s(
  <>
    <circle cx="12" cy="7.5" r="4" />
    <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" />
  </>,
);
export const Loader2 = s(
  <>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </>,
);
export const Card = s(
  <>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20M6 15h4" />
  </>,
);
export const Alert = s(
  <>
    <path d="M12 9v4M12 17h.01M10.3 3.9L2.4 18a2 2 0 001.7 3h15.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
  </>,
);
export const Clock = s(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);
export const Info = s(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M11 12h1v4h1" />
  </>,
);
export const Check = s(<path d="M20 6L9 17l-5-5" />);
export const CheckBig = s(<path d="M5 12l5 5L20 7" />);
export const Chart = s(
  <>
    <path d="M3 3v18h18" />
    <path d="M7 14l4-4 3 3 5-6" />
  </>,
);
export const Coins = s(
  <>
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
  </>,
);
export const Moon = s(<path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />);
export const Bolt = s(<path d="M13 2L3 14h7l-1 8 10-12h-7z" />);
export const Calendar = s(
  <>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M3 9h18M8 2v4M16 2v4" />
  </>,
);
export const Video = s(
  <>
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="M16 10l6-3v10l-6-3" />
  </>,
);
export const Arrow = s(<path d="M5 12h14M13 6l6 6-6 6" />);
export const ArrowDown = s(<path d="M12 5v14M19 12l-7 7-7-7" />);
export const LogOut = s(
  <>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </>,
);
export const Eye = s(
  <>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
export const EyeOff = s(
  <>
    <path d="M17.9 17.9A10.6 10.6 0 0112 19c-7 0-11-7-11-7a19.4 19.4 0 015.1-5.6M9.9 4.2A9.9 9.9 0 0112 4c7 0 11 7 11 7a19.4 19.4 0 01-3 3.9M14.1 14.1a3 3 0 10-4.2-4.2" />
    <path d="M1 1l22 22" />
  </>,
);
export const RefreshCw = s(
  <>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </>
);
export const Copy = s(
  <>
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </>
);
export const ClipboardPaste = s(
  <>
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M8 4H6a2 2 0 00-2 2v14a2 2 0 002 2h6" />
    <path d="M16 4h2a2 2 0 012 2v3" />
    <path d="M21 14H11" />
    <path d="M15 10l-4 4 4 4" />
  </>
);
export const Trash = s(
  <>
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </>
);
export const Linkedin = s(
  <>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </>
);

export const Edit = s(
  <>
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </>,
);

export const Search = s(
  <>
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </>,
);

export const Mail = s(
  <>
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="M22 7l-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
  </>,
);

export const Phone = s(
  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z" />
);

export const Briefcase = s(
  <>
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </>
);

export const Link2 = s(
  <>
    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
    <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </>
);

export const Filter = s(
  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
);

export const Sparkles = s(
  <>
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
  </>
);

export const ChevronDown = s(
  <path d="m6 9 6 6 6-6" />
);

export const ExternalLink = s(
  <>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </>
);

export const Send = s(
  <>
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </>
);

export const Save = s(
  <>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </>
);

export const HelpCircle = s(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </>
);

export const MessageSquare = s(
  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
);

export const X = s(<path d="M18 6L6 18M6 6l12 12" />);

export const ArrowLeft = s(
  <>
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </>
);

export const ArrowRight = s(
  <>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </>
);

export const Bug = s(
  <>
    <path d="m8 2 1.88 1.88" />
    <path d="M14.12 3.88 16 2" />
    <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
    <path d="M12 20v-9" />
    <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
    <path d="M6 13H2" />
    <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
    <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
    <path d="M22 13h-4" />
    <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
  </>,
);

